<template>
  <div :class="$style.container">
    <div :class="$style.header">
      <b :class="$style.headerTitle">転職活動をより良くするためのアクション</b>
      <button
        :class="[$style.toggleButton, { [$style.rotated]: !isOpen }]"
        type="button"
        :aria-expanded="isOpen"
        :aria-controls="bodyId"
        @click="toggle"
      >
        <img :src="toggleIcon" alt="アクションリストの開閉" />
      </button>
    </div>

    <!-- TODO: shared/loading が作成されたら共通化する -->
    <div v-if="loading" :class="$style.actionListLoading" aria-busy="true" aria-live="polite">
      <div :class="$style.actionListLoadingSpinner" role="status" />
    </div>
    <div v-else :id="bodyId" :class="[$style.actionList, { [$style.isClosed]: !isOpen }]">
      <button
        v-for="action in actions"
        :key="action.key"
        :class="$style.item"
        type="button"
        @click="handleAction(action)"
      >
        <div :class="$style.labelWrap">
          <div :class="$style.titleWrap">
            <b :class="$style.title">{{ action.title }}</b>
          </div>
          <div v-if="action.description" :class="$style.descriptionWrap">
            <div :class="$style.description">{{ action.description }}</div>
          </div>
        </div>
        <img :class="$style.linkIcon" :src="cardNextIcon" alt="リンク先へ" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import toggleIcon from '@/assets/images/icons/action-list-toggle.svg';
import cardNextIcon from '@/assets/images/icons/card-next.svg';
import { baseActions } from './actionListActions';
import type { ActionItem, ActionKey, ActionListProps } from './types';

const props = withDefaults(defineProps<ActionListProps>(), {
  loading: false,
});
const emit = defineEmits<{
  (e: 'select', key: ActionKey): void;
}>();

const bodyId = `top-auth-action-list-${crypto.randomUUID()}`;
const isOpen = ref(true);

const actions = computed<ActionItem[]>(() => {
  const platform = props.userPlatform;
  const showSteps = props.shouldShowStepsRegistration === true;

  const visibility: Record<ActionKey, boolean> = {
    // 求人会員登録: エージェントのみのときに表示
    jobEntry: platform === 'agent',
    // エージェントサービス登録: ダイレクトのみのときに表示
    agentService: platform === 'direct',
    // 基礎情報の登録: should_show_steps_registration が true のときのみ表示（RegistrationModal と同様）
    basicInfo: showSteps,
    agentInterview: platform === 'agent' || platform === 'both',
    ...props.visibleActions,
  };

  return baseActions.filter((action) => visibility[action.key]);
});

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const handleAction = (action: ActionItem) => {
  emit('select', action.key);
};
</script>

<style module lang="scss">
@import '@/assets/css/_variables.scss';

.container {
  width: 100%;
  max-width: 760px;
  position: relative;
  border-radius: 12px;
  background-color: #e8f5ff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 24px;
  box-sizing: border-box;
  gap: 16px;
  text-align: left;
  font-size: 14px;
  color: #1e1e1e;
  font-family: 'Noto Sans JP', sans-serif;
}

.header {
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.headerTitle {
  letter-spacing: 0.85px;
  line-height: 21px;
}

.toggleButton {
  display: none;
  height: 25px;
  width: 25px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  border: none;
  padding: 0;
  background: #ffffff;
  cursor: pointer;
}

.toggleButton img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease;
}

.rotated img {
  transform: rotate(-90deg);
}

.actionList {
  align-self: stretch;
  border-radius: 8px;
  background-color: #f3f4f6;
  border: 1px solid #f3f4f6;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #0090c2;
}

.isClosed {
  display: none;
}

/* PC（560px以上）ではトグル非表示のため、閉じた状態でもリストを常に表示 */
@media (min-width: 561px) {
  .actionList.isClosed {
    display: flex;
  }
}

/* TODO: shared/loading が作成されたら共通化する - 白背景・テーマ primary スピナー */
.actionListLoading {
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  border-radius: 8px;
  background-color: #fff;
}

.actionListLoadingSpinner {
  display: inline-block;
  width: 5rem;
  height: 5rem;
  vertical-align: -0.125em;
  border: 0.4em solid currentcolor;
  border-right-color: transparent;
  border-radius: 50%;
  color: $primary;
  animation: action-list-spinner-rotate 0.75s linear infinite;
}

@keyframes action-list-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}

.item {
  width: 100%;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  gap: 20px;
  min-height: 45px;
  border: none;
  cursor: pointer;
  text-align: left;
  color: #0090c2;
}

.item:last-of-type {
  border-bottom: none;
}

.labelWrap {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.titleWrap {
  position: relative;
  min-width: 180px;
}

.title {
  letter-spacing: -0.15px;
  line-height: 21px;
}

.descriptionWrap {
  position: relative;
  font-size: 12px;
  color: #545454;
}

.description {
  line-height: 18px;
  white-space: normal;
}

.linkIcon {
  height: 16px;
  width: 16px;
  flex-shrink: 0;
}

@media (max-width: 560px) {
  .container {
    padding: 14px 11px;
    gap: 16px;
  }

  .headerTitle {
    font-size: 14px;
    line-height: 21px;
  }

  .toggleButton {
    display: inline-flex;
  }

  .actionList {
    border: none;
    background-color: transparent;
  }

  .item {
    width: 100%;
    background-color: #fff;
    border-bottom: 1px solid #f0f0f0;
    padding: 8px 20px;
    gap: 15.8px;
  }

  .labelWrap {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .titleWrap {
    min-width: auto;
  }

  .description {
    white-space: pre-line;
  }
}
</style>
